/* eslint-disable max-lines */
import * as moment from "moment";

import { MedicalTypeId } from "@teamprevent/common/healthSurveillance";
import { TaskType } from "@teamprevent/common/consultations/models";
import { TaskModel, Link, TaskTypes, TaskTypeLabel, TaskCategories } from "@teamprevent/common/task/taskActions/models";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";
import { MedicalType } from "@teamprevent/common/healthSurveillance/models";
import { isClinicalNotesBeingCompletedInAdvance } from "@teamprevent/common/questionnaires/components/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { MedicalOutcomeFormModel } from "../healthSurveillance/review/models";
import { AddNextTaskFormModel } from "../tasks/models";
import { FollowUpTask } from "../immunisationReview/components/FollowUpTasks";
import { EppDecision } from "../immunisations/model";

import { Location } from "redux-little-router";

export interface TasksUpdatedSuccessResult {
    ohCaseId: string;
    tasks: TaskModel[];
}

export interface CancelTaskModel {
    readonly ohCaseId: string;
    readonly taskId: string;
    readonly tasks: TaskModel[];
}

export interface ImmunisationTaskModel extends TaskModel {
    readonly immunisationTypeTasks: Link[];
}

export interface HealthSurveillanceTaskModel extends TaskModel {
    readonly medicalTypes: MedicalTypeId[];
}

export type CreateImmunisationTaskDto = CreateTaskDto & { immunisationTypeTasks: Link[] };

export interface CreateTaskDto {
    readonly taskType: string;
    readonly description: string;
    readonly taskCodeId: string | null;
    // Local DateTime
    readonly localDueDate?: Date;
    readonly notes: string | null;
    readonly additionalNotes?: string;
    readonly caseId: string;
    readonly immunisationTypeTasks?: Link[] | null;
    readonly medicalTypeIds?: string[];
    readonly immunisationTypeTaskIds?: string[];
    readonly assignedUsers?: Link[];
    readonly eppDecision?: EppDecision;
    readonly precedingTaskId?: string;
    readonly appointmentId?: string;
    readonly completed?: boolean;
    readonly createdBy?: Link;
    readonly amount?: number;
}

export interface CreateTaskModel {
    readonly taskType: string;
    readonly description: string;
    readonly taskCodeId: string;
    // Local DateTime
    readonly dueDate?: Date;
    readonly notes: string;
    readonly caseId: string;
    readonly medicalTypes?: string[];
    readonly immunisationTypeTaskIds?: string[];
    readonly assignedUsers?: Link[];
    readonly eppDecision?: EppDecision;
    readonly precedingTaskId?: string;
    readonly amount?: number;
}

/**
 * @deprecated Prefer using CreateTaskModel or CreateTaskDto which represent their c# namesakes and use `Date` instead of `string` for `dueDate` property.
 * This interface is a legacy type and used as a weird hybrid of both CreateTaskModel and CreateTaskDto making things confusing.
 * Convert the `Date` to string on the api layer.
 */
export interface AddTaskModel {
    readonly type: string;
    readonly description: string;
    readonly taskCodeId: string;
    // Local DateTime
    readonly dueDate?: string;
    readonly notes: string;
    readonly caseId: string;
    readonly immunisationTypeTasks?: Link[];
    readonly medicalIds?: string[];
    readonly immunisationTypeTaskIds?: string[];
    readonly assignedUsers?: Link[];
    readonly eppDecision?: EppDecision;
    readonly precedingTaskId?: string;
    readonly amount?: number;
}

export interface AddFileNoteModel {
    notes: string;
}

export interface OhCaseSearchDetails {
    readonly employeeId: string;
    readonly employeeName: string;
    readonly customerId: string;
    readonly ohCaseId: string;
}

export interface AddTaskFormModel {
    taskType: Omit<TaskType, "taskCategories"> | null;
    taskCode?: string;
    caseType?: string;
    dueDate: moment.Moment | null;
    notes: string;
    medicalIds?: string[];
    immunisationTypeTaskIds?: string[];
    assignedUsers?: Link[];
    eppDecision?: EppDecision;
}

export interface AddFollowUpTaskModel {
    task: AddTaskFormModel & { immunisationTypeTasks: Link[] };
    isValid: boolean;
}

export { TaskType };

export interface TaskTypeFilters {
    type: string | null;
    label?: TaskTypeLabel | null;
    taskCategories: string;
}

export interface TaskState {
    readonly taskTypes: TaskType[];
}

export interface TaskActionProps {
    customerId: string;
    task: TaskModel;
    onConfirmation?: OnConfirmationTaskActionProps | null;
    delegate?: (taskId: string) => void;
    preventNavigation?: boolean;
    allowActionWhenCancelled?: boolean;
}

export interface OnConfirmationTaskActionProps {
    title: string;
    content: string;
}

export interface GenericTaskActionProps {
    content: string;
    taskRoute: string;
}

export type MedicalOutcomes = (MedicalOutcomeFormModel & { medicalType: MedicalType })[];
export type TaskModels = AddNextTaskFormModel[];

export interface OutcomesData {
    outcomes: MedicalOutcomes;
}

export interface TasksData {
    tasks: TaskModels;
}

export interface ReviewHealthSurveillanceResultsSaveModel {
    eTag?: string;
    outcomesData: OutcomesData;
    tasksData: TasksData;
}

export type AppState = { tasks: TaskState; router: Location; loading: any };

// This should only take ServerModelWithDateStrings<TaskModel> as a parameter but it's called in a few too many places for
// it to be practical to remove `| TaskModel` just yet
export function parseTaskModel(task: ServerModelWithDateStrings<TaskModel> | TaskModel): TaskModel {
    return {
        ...task,
        dueDate: task.dueDate ? moment(task.dueDate) : undefined,
        dateCreated: moment(task.dateCreated ?? undefined),
        completedDate: task.completedDate ? moment(task.completedDate) : null,
        actualCompletionDate: task.actualCompletionDate ? moment(task.actualCompletionDate) : undefined,
        earlierAppointmentDate: task.earlierAppointmentDate ? moment(task.earlierAppointmentDate) : undefined
    };
}

export const mapToCreateTaskModel = (addTaskFormModel: AddTaskFormModel, ohCaseId: string): AddTaskModel => ({
    type: addTaskFormModel.taskType.type,
    description: addTaskFormModel.taskType.description,
    taskCodeId: addTaskFormModel.taskCode,
    dueDate: addTaskFormModel.dueDate?.isValid() ? addTaskFormModel.dueDate.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS) : undefined,
    notes: addTaskFormModel.notes,
    caseId: ohCaseId,
    immunisationTypeTaskIds: addTaskFormModel.immunisationTypeTaskIds,
    medicalIds: addTaskFormModel.medicalIds,
    assignedUsers: addTaskFormModel.assignedUsers,
    eppDecision: addTaskFormModel.eppDecision
});

export const mapAddTaskFormModelToCreateTaskDto = (addTaskFormModel: AddTaskFormModel, ohCaseId: string): CreateTaskDto => ({
    taskType: addTaskFormModel.taskType.type,
    description: addTaskFormModel.taskType.description,
    taskCodeId: addTaskFormModel.taskCode ,
    localDueDate: addTaskFormModel.dueDate?.toDate(),
    notes: addTaskFormModel.notes,
    caseId: ohCaseId,
    medicalTypeIds: addTaskFormModel.medicalIds,
    assignedUsers: addTaskFormModel.assignedUsers
});

export const mapAddTaskFormModelToCreateTaskModel = (addTaskFormModel: AddTaskFormModel, ohCaseId: string): CreateTaskModel => ({
    taskType: addTaskFormModel.taskType.type,
    description: addTaskFormModel.taskType.description,
    taskCodeId: addTaskFormModel.taskCode ,
    dueDate: addTaskFormModel.dueDate?.toDate(),
    notes: addTaskFormModel.notes,
    caseId: ohCaseId,
    immunisationTypeTaskIds: addTaskFormModel.immunisationTypeTaskIds,
    medicalTypes: addTaskFormModel.medicalIds,
    assignedUsers: addTaskFormModel.assignedUsers,
    eppDecision: addTaskFormModel.eppDecision,
});

export const mapToFollowUpTask = (addTaskFormModel: AddNextTaskFormModel, isValid: boolean): FollowUpTask => ({
    isHidden: false,
    isValid,
    task: {
        immunisationTypeTasks: addTaskFormModel.task.immunisationTypeTaskIds ?? [],
        taskType: addTaskFormModel.task.taskType?.type,
        assignedUsers: addTaskFormModel.task.assignedUsers,
        dueDate: addTaskFormModel.task.dueDate,
        notes: addTaskFormModel.task.notes,
        taskCodeId: addTaskFormModel.task.taskCode,
    }
});

export const isImmunisationTask = (task: TaskModel): task is ImmunisationTaskModel => {
    return !!(task as ImmunisationTaskModel).immunisationTypeTasks;
};

export const isHealthSurveillanceTask = (task: TaskModel): task is HealthSurveillanceTaskModel => {
    return !!(task as HealthSurveillanceTaskModel).medicalTypes;
};

export interface AmendReportProps {
    task: TaskModel;
    customerId: string;
}

export enum RebookingReasons {
    AppointmentNoLongerNecessary = "AppointmentNoLongerNecessary",
    ClinicChangedByTeamPrevent = "ClinicChangedByTeamPrevent",
    EmployeeMadeContactAppointmentRearranged = "EmployeeMadeContactAppointmentRearranged",
    EmployeeSickAppointmentRearranged = "EmployeeSickAppointmentRearranged",
    EmployeeUnavailableOrAbsentDueToCovid19 = "EmployeeUnavailableOrAbsentDueToCovid19",
    IncorrectlyBookedByTeamPrevent = "IncorrectlyBookedByTeamPrevent",
    ManagerMadeContactAppointmentRearranged = "ManagerMadeContactAppointmentRearranged",
    NurseNeededAtAMeeting = "NurseNeededAtAMeeting",
    NurseSickClinicRearranged = "NurseSickClinicRearranged",
    OtherPleaseSpecifyInComments = "OtherPleaseSpecifyInComments"
}

export const rebookingReasonsOptions: DropdownOption[] = [
    { text: "Appointment No Longer Necessary", value: RebookingReasons.AppointmentNoLongerNecessary },
    { text: "Clinic Changed by Optima Health", value: RebookingReasons.ClinicChangedByTeamPrevent },
    { text: "Employee Made Contact - Appointment Rearranged", value: RebookingReasons.EmployeeMadeContactAppointmentRearranged },
    { text: "Employee Sick - Appointment Rearranged", value: RebookingReasons.EmployeeSickAppointmentRearranged },
    { text: "Employee Unavailable / Absent Due to COVID-19", value: RebookingReasons.EmployeeUnavailableOrAbsentDueToCovid19 },
    { text: "Incorrectly Booked by Optima Health", value: RebookingReasons.IncorrectlyBookedByTeamPrevent },
    { text: "Manager Made Contact - Appointment Rearranged", value: RebookingReasons.ManagerMadeContactAppointmentRearranged },
    { text: "Nurse Needed at a Meeting", value: RebookingReasons.NurseNeededAtAMeeting },
    { text: "Nurse Sick - Clinic Rearranged", value: RebookingReasons.NurseSickClinicRearranged },
    { text: "Other - Please Specify in Comments", value: RebookingReasons.OtherPleaseSpecifyInComments }
];

export const taskTypeFilters: TaskTypeFilters = {
    label: null,
    type: "",
    taskCategories: ""
};

export const ExcludeBbvTaskTypes = [
    TaskTypes.BookSerumSaveTask,
    TaskTypes.HepBBoosterTask,
    TaskTypes.SerumSaveTask,
    TaskTypes.SourceUnknown6WeekBloodTestTask,
    TaskTypes.SourceUnknownBook6WeekBloodTestTask,
    TaskTypes.SourceUnknownBook12WeekBloodTestTask,
    TaskTypes.SourceUnknown12WeekBloodTestTask,
    TaskTypes.SourceUnknownBook24WeekBloodTestTask,
    TaskTypes.SourceUnknown24WeekBloodTestTask
];

export const allowedToRemoveAppointment = (task: TaskModel): OnConfirmationTaskActionProps | undefined => {
    if (isConsultationTaskWithAppointment(task) && isClinicalNotesBeingCompletedInAdvance(task)) {
        return {
            title: task.description,
            content: "You are about to start an appointment booked in the future. If you complete " +
                "this appointment you will remove the booking from the calendar. You can start your " +
                "notes prior to the appointment but if you COMPLETE them the booking will be " +
                "cancelled and the space will be rebooked. Do you wish to proceed?"
        };
    }

    return undefined;
};

export const isConsultationTaskWithAppointment = (task: TaskModel) => {
    return task.taskCategories.includes(TaskCategories.ConsultationTask) && task.appointmentId !== null && task.dueDate && task.dueDate.isAfter(moment());
};
